import React from 'react';
import classNames from 'classnames';
import {
  FiCheckSquare,
  FiCopy,
  FiCalendar,
  FiFlag,
  FiMoreVertical,
} from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { StatusBadge, statusBadgeTones } from '@/components/StatusBadge';
import { InitialsBadge } from '@/components/InitialsBadge';
import { Button, buttonVariants } from '@/components/Button';
import { stripHtml } from '@/utils/html';
import styles from './Tasks.module.scss';

export const STATUS_TONE = {
  DONE: statusBadgeTones.success,
  'IN PROGRESS': statusBadgeTones.info,
  QA: statusBadgeTones.warning,
  'TO DO': statusBadgeTones.error,
};

export const PRIORITY_TONE = {
  P1: statusBadgeTones.error,
  P2: statusBadgeTones.orange,
  P3: statusBadgeTones.purple,
  P4: statusBadgeTones.default,
};

function TitleCell({ row }) {
  const tone = STATUS_TONE[row.Status] || statusBadgeTones.default;
  return (
    <div className={styles.taskCell}>
      <span className={classNames(styles.taskIcon, styles[`taskIcon_${tone}`])}>
        <FiCheckSquare size={15} />
      </span>
      <div className={styles.taskCellText}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Semibold}
        >
          {row.Title}
        </Text>
        {row.Description && (
          <Text
            variant={textVariantsEnum.Text_xs}
            color="gray-500"
            className={styles.taskCellDesc}
          >
            {stripHtml(row.Description)}
          </Text>
        )}
      </div>
    </div>
  );
}

function TaskIdCell({ row, onCopyId }) {
  return (
    <button
      type="button"
      className={styles.taskIdCell}
      onClick={(e) => {
        e.stopPropagation();
        onCopyId(row.id);
      }}
      aria-label="Copy task ID"
    >
      <Text variant={textVariantsEnum.Text_xs} className={styles.taskIdText}>
        {row.id}
      </Text>
      <FiCopy size={12} />
    </button>
  );
}

function AssigneeCell({ row, memberDetails }) {
  const member = memberDetails?.[row.Assignee];
  const name = member?.userName || member?.userEmailid || row.Assignee || '—';
  const role = member?.userType || member?.role || '';
  return (
    <div className={styles.assigneeCell}>
      <InitialsBadge label={name} twoLetter />
      <div className={styles.assigneeCellText}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {name}
        </Text>
        {role && (
          <Text variant={textVariantsEnum.Text_xs} color="gray-500">
            {role}
          </Text>
        )}
      </div>
    </div>
  );
}

function EtaCell({ row, formatEtaDate, getEtaUrgency }) {
  const date = formatEtaDate(row.ETA);
  if (!date) {
    return (
      <Text variant={textVariantsEnum.Text_sm} color="gray-500">
        —
      </Text>
    );
  }
  const { urgency, label } = getEtaUrgency(date);
  return (
    <div className={classNames(styles.etaCell, styles[`etaCell_${urgency}`])}>
      <div className={styles.etaDate}>
        <FiCalendar size={13} />
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
        </Text>
      </div>
      <Text
        variant={textVariantsEnum.Text_xs}
        fontWeight={fontWeightEnum.Semibold}
        className={styles.etaLabel}
      >
        {label}
      </Text>
    </div>
  );
}

export const tasksTableConfig = ({
  memberDetails,
  formatEtaDate,
  getEtaUrgency,
  onCopyId,
  onActionsButtonClick,
}) => [
  {
    id: 'title',
    name: 'Title',
    style: { width: '32%' },
    cellRenderer: (row) => <TitleCell row={row} />,
  },
  {
    id: 'taskId',
    name: 'Task ID',
    sortable: true,
    sortKey: 'id',
    style: { width: '12%' },
    cellRenderer: (row) => <TaskIdCell row={row} onCopyId={onCopyId} />,
  },
  {
    id: 'status',
    name: 'Status',
    sortable: true,
    sortKey: 'Status',
    style: { width: '11%' },
    cellRenderer: (row) => (
      <StatusBadge
        label={row.Status}
        tone={STATUS_TONE[row.Status] || statusBadgeTones.default}
        dot
      />
    ),
  },
  {
    id: 'assignee',
    name: 'Assignee',
    sortable: true,
    sortKey: 'Assignee',
    style: { width: '17%' },
    cellRenderer: (row) => (
      <AssigneeCell row={row} memberDetails={memberDetails} />
    ),
  },
  {
    id: 'eta',
    name: 'ETA',
    sortable: true,
    sortKey: 'ETA',
    style: { width: '14%' },
    cellRenderer: (row) => (
      <EtaCell
        row={row}
        formatEtaDate={formatEtaDate}
        getEtaUrgency={getEtaUrgency}
      />
    ),
  },
  {
    id: 'priority',
    name: 'Priority',
    style: { width: '9%' },
    cellRenderer: (row) => (
      <StatusBadge
        label={row.Priority}
        tone={PRIORITY_TONE[row.Priority] || statusBadgeTones.default}
        icon={<FiFlag size={11} />}
      />
    ),
  },
  {
    id: 'actions',
    name: '',
    style: { width: '5%' },
    cellRenderer: (row) => (
      <Button
        icon={<FiMoreVertical size={14} />}
        variant={buttonVariants.outline}
        ariaLabel="Task actions"
        onClick={(e) => {
          e.stopPropagation();
          onActionsButtonClick(e, row);
        }}
      />
    ),
  },
];
